import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import { API, auth, inputCls, Field, Modal, PageHead, PrimaryBtn, Paginator, errMsg } from './finance/shared';
import { EmptyState } from '../../components/dashboard/TodaySchedule';
import Icon from '../../components/dashboard/DashboardIcons';
import SeasonsSection from './SeasonsSection';
import CategoryPlayersModal from './players/CategoryPlayersModal';

const Kpi = ({ label, value, accent }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">{label}</div><div className={`text-xl font-bold ${accent || 'text-slate-900'}`}>{value}</div></div>
);

export default function TeamsPage() {
  const { club, selectedSeasonId } = useOutletContext() || {};
  const seasonQS = selectedSeasonId && selectedSeasonId !== 'all' ? `?season_id=${selectedSeasonId}` : '';
  const [params, setParams] = useSearchParams();
  const rawView = params.get('view');
  const view = rawView === 'categories' ? 'categories' : rawView === 'seasons' ? 'seasons' : 'teams';
  const setView = (v) => setParams((prev) => { const n = new URLSearchParams(prev); n.set('view', v); return n; });

  const [teams, setTeams] = useState([]);
  const [cats, setCats] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(() => params.get('q') || '');
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState(null);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3000); };

  const [teamModal, setTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamForm, setTeamForm] = useState({});
  const [teamFormError, setTeamFormError] = useState(null);
  const [confirmDelTeam, setConfirmDelTeam] = useState(null);

  const [catModal, setCatModal] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({});
  const [catFormError, setCatFormError] = useState(null);
  const [confirmDelCat, setConfirmDelCat] = useState(null);
  const [viewingCat, setViewingCat] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [t, c, p] = await Promise.allSettled([axios.get(`${API}/teams${seasonQS}`, auth()), axios.get(`${API}/categories${seasonQS}`, auth()), axios.get(`${API}/players`, auth())]);
    if (t.status === 'fulfilled') setTeams(t.value.data || []);
    if (c.status === 'fulfilled') setCats(c.value.data || []);
    if (p.status === 'fulfilled') setPlayers(Array.isArray(p.value.data) ? p.value.data : []);
    setLoading(false);
  }, [seasonQS]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSearch(''); setPage(1); }, [view]);

  const countFor = (catId) => players.filter((p) => p.category_id === catId).length;
  const clubTeams = teams.filter((t) => t.is_club_team);

  // ---- الفرق ----
  const openTeamForm = (t) => { setEditingTeam(t || null); setTeamForm(t ? { ...t } : { name: '', description: '', is_club_team: false, home_stadium: '', logo_url: '' }); setTeamFormError(null); setTeamModal(true); };
  const submitTeam = async (e) => {
    e.preventDefault();
    if (!teamForm.name) { setTeamFormError('اسم الفريق مطلوب'); return; }
    const payload = { name: teamForm.name, description: teamForm.description || '', is_club_team: Boolean(teamForm.is_club_team), home_stadium: teamForm.home_stadium || null, logo_url: teamForm.logo_url?.trim() || null, logo_file_id: teamForm.logo_file_id || null };
    try {
      if (editingTeam) await axios.put(`${API}/teams/${editingTeam.id}`, payload, auth());
      else await axios.post(`${API}/teams`, payload, auth());
      setTeamModal(false); notify('تم الحفظ بنجاح'); load();
    } catch (err) { setTeamFormError(errMsg(err, 'فشل الحفظ')); }
  };
  const removeTeam = async () => {
    try { await axios.delete(`${API}/teams/${confirmDelTeam.id}`, auth()); notify('تم حذف الفريق'); setConfirmDelTeam(null); load(); }
    catch (err) { notify(errMsg(err, 'تعذر الحذف — قد يكون الفريق مرتبطاً بمباريات')); setConfirmDelTeam(null); }
  };

  // ---- الفئات ----
  const openCatForm = (c) => { setEditingCat(c || null); setCatForm(c ? { ...c } : { name: '', description: '', min_age: '', max_age: '' }); setCatFormError(null); setCatModal(true); };
  const submitCat = async (e) => {
    e.preventDefault();
    if (!catForm.name) { setCatFormError('اسم الفئة مطلوب'); return; }
    if (!catForm.min_age || !catForm.max_age) { setCatFormError('العمر الأدنى والأقصى حقلان مطلوبان لضمان اقتراح الفئة الصحيح للاعبين'); return; }
    const payload = { name: catForm.name, description: catForm.description || '', min_age: parseInt(catForm.min_age), max_age: parseInt(catForm.max_age) };
    try {
      if (editingCat) await axios.put(`${API}/categories/${editingCat.id}`, payload, auth());
      else await axios.post(`${API}/categories`, payload, auth());
      setCatModal(false); notify('تم الحفظ'); load();
    } catch (err) { setCatFormError(errMsg(err, 'فشل الحفظ')); }
  };
  const removeCat = async () => {
    try { await axios.delete(`${API}/categories/${confirmDelCat.id}`, auth()); notify('تم الحذف'); setConfirmDelCat(null); load(); }
    catch (err) { notify(errMsg(err, 'تعذر الحذف')); setConfirmDelCat(null); }
  };

  const filteredTeams = teams.filter((t) => !search.trim() || t.name?.includes(search.trim()));
  const filteredCats = cats.filter((c) => !search.trim() || c.name?.includes(search.trim()));
  const pagedTeams = filteredTeams.slice((page - 1) * 12, page * 12);

  return (
    <div className="mx-auto max-w-7xl space-y-4" data-testid="teams-categories-page">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      <PageHead title="الفرق والفئات" desc="فرق المنشأة والفرق المنافسة، والفئات السنية للاعبين في مكان واحد"
        action={view === 'teams'
          ? <PrimaryBtn onClick={() => openTeamForm(null)} data-testid="team-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة فريق</PrimaryBtn>
          : view === 'categories'
          ? <PrimaryBtn onClick={() => openCatForm(null)} data-testid="cat-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة فئة</PrimaryBtn>
          : null} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="إجمالي الفرق" value={teams.length} />
        <Kpi label="فرق المنشأة" value={clubTeams.length} accent="text-teal-700" />
        <Kpi label="إجمالي الفئات" value={cats.length} />
        <Kpi label="اللاعبون المسجلون" value={players.length} />
      </div>

      <div className="flex gap-1 rounded-xl border border-slate-200 bg-white p-1" data-testid="teams-cats-view-toggle">
        <button onClick={() => setView('teams')} className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${view === 'teams' ? 'bg-[#16233b] text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="teams-view-btn">الفرق</button>
        <button onClick={() => setView('categories')} className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${view === 'categories' ? 'bg-[#16233b] text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="categories-view-btn">الفئات</button>
        <button onClick={() => setView('seasons')} className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${view === 'seasons' ? 'bg-[#16233b] text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="seasons-view-btn">المواسم والترحيل</button>
      </div>

      {view === 'seasons' ? <SeasonsSection notify={notify} /> : (
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
            <Icon name="search" className="w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder={view === 'teams' ? 'بحث عن فريق...' : 'بحث عن فئة...'} className="w-44 bg-transparent text-sm outline-none" data-testid="teams-cats-search" />
          </div>
          <span className="mr-auto text-xs text-slate-400">{view === 'teams' ? filteredTeams.length : filteredCats.length} {view === 'teams' ? 'فريق' : 'فئة'}</span>
        </div>

        {loading ? <div className="grid grid-cols-2 gap-3 p-4 lg:grid-cols-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-24 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : view === 'teams' ? (
            pagedTeams.length === 0 ? <div className="p-6"><EmptyState icon="userGroup" text="لا توجد فرق مطابقة" testId="teams-empty" /></div> : (
              <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {pagedTeams.map((t) => (
                  <div key={t.id} className="rounded-xl border border-slate-100 bg-slate-50/50 p-4 transition-colors hover:border-teal-200" data-testid="team-card">
                    <div className="flex items-center gap-3">
                      {t.logo_url || t.logo_file_id ? (
                        <img src={t.logo_file_id ? `${API}/files/${t.logo_file_id}` : t.logo_url} alt="" className="h-10 w-10 rounded-lg bg-white object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#16233b]/10 text-[#16233b]"><Icon name="userGroup" className="w-5 h-5" /></div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-bold text-slate-800">{t.name}</div>
                        {t.is_club_team && <span className="rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-bold text-teal-700" data-testid="club-team-badge">فريق المنشأة</span>}
                      </div>
                    </div>
                    {t.home_stadium && <div className="mt-2 text-xs text-slate-500">الملعب: {t.home_stadium}</div>}
                    <div className="mt-3 flex justify-end gap-1">
                      <button onClick={() => openTeamForm(t)} className="rounded-lg p-1.5 text-slate-500 hover:bg-white hover:text-teal-700" data-testid="team-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                      <button onClick={() => setConfirmDelTeam(t)} className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="team-del-btn"><Icon name="x" className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            filteredCats.length === 0 ? <div className="p-6"><EmptyState icon="tag" text="لا توجد فئات بعد — أضف أول فئة" testId="cats-empty" /></div> : (
              <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredCats.map((c) => (
                  <div key={c.id} className="rounded-xl border border-slate-100 bg-slate-50/50 p-4" data-testid="category-card">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm font-bold text-slate-900">{c.name}</div>
                        <div className="mt-0.5 text-xs text-slate-500">
                          {c.min_age ? `من ${c.min_age} سنة` : ''}{c.max_age ? ` إلى ${c.max_age} سنة` : c.min_age ? ' فما فوق' : ''}
                        </div>
                      </div>
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-[#16233b]"><Icon name="tag" className="w-4 h-4" /></span>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-teal-50 px-2.5 py-1 text-xs font-bold text-teal-700" data-testid="cat-player-count">{countFor(c.id)} لاعب</span>
                        <button onClick={() => setViewingCat(c)} className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-[#16233b] hover:border-teal-300 hover:text-teal-700" data-testid="cat-view-players-btn">
                          <Icon name="userGroup" className="w-3.5 h-3.5" /> عرض اللاعبين
                        </button>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => openCatForm(c)} className="rounded-lg p-1.5 text-slate-500 hover:bg-white" data-testid="cat-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                        <button onClick={() => setConfirmDelCat(c)} className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="cat-del-btn"><Icon name="x" className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        {view === 'teams' && <Paginator page={page} total={filteredTeams.length} limit={12} onChange={setPage} />}
      </div>
      )}

      <Modal open={teamModal} title={editingTeam ? 'تعديل الفريق' : 'إضافة فريق'} onClose={() => setTeamModal(false)}>
        {teamFormError && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="team-form-error">{teamFormError}</div>}
        <form onSubmit={submitTeam} className="space-y-3">
          <Field label="اسم الفريق" required><input className={inputCls} value={teamForm.name || ''} onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })} data-testid="team-input-name" /></Field>
          <Field label="الوصف"><input className={inputCls} value={teamForm.description || ''} onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })} /></Field>
          <Field label="الملعب"><input className={inputCls} value={teamForm.home_stadium || ''} onChange={(e) => setTeamForm({ ...teamForm, home_stadium: e.target.value })} /></Field>
          <Field label="رابط صورة الشعار">
            <div className="flex items-center gap-2">
              <input className={inputCls} dir="ltr" placeholder="https://..." value={teamForm.logo_url || ''} onChange={(e) => setTeamForm({ ...teamForm, logo_url: e.target.value })} data-testid="team-input-logo-url" />
              {teamForm.logo_url?.trim() && <img src={teamForm.logo_url.trim()} alt="" className="h-9 w-9 shrink-0 rounded-lg border border-slate-200 bg-white object-contain" onError={(e) => { e.target.style.display = 'none'; }} />}
            </div>
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={Boolean(teamForm.is_club_team)} onChange={(e) => setTeamForm({ ...teamForm, is_club_team: e.target.checked })} data-testid="team-input-clubteam" />
            هذا فريق تابع للمنشأة (يُستخدم لحساب النتائج والانتصارات)
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setTeamModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="team-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmDelTeam)} title="حذف الفريق" onClose={() => setConfirmDelTeam(null)}>
        <p className="text-sm text-slate-600">حذف «{confirmDelTeam?.name}» قد يؤثر على المباريات المرتبطة به. هل أنت متأكد؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDelTeam(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={removeTeam} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="team-confirm-del">حذف</button>
        </div>
      </Modal>

      <Modal open={catModal} title={editingCat ? 'تعديل الفئة' : 'إضافة فئة'} onClose={() => setCatModal(false)}>
        {catFormError && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="cat-form-error">{catFormError}</div>}
        <form onSubmit={submitCat} className="space-y-3">
          <Field label="اسم الفئة" required><input className={inputCls} value={catForm.name || ''} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="cat-input-name" /></Field>
          <Field label="الوصف"><input className={inputCls} value={catForm.description || ''} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="العمر الأدنى" required><input className={inputCls} dir="ltr" type="number" min="0" data-testid="cat-input-min-age" value={catForm.min_age || ''} onChange={(e) => setCatForm({ ...catForm, min_age: e.target.value })} /></Field>
            <Field label="العمر الأقصى" required><input className={inputCls} dir="ltr" type="number" min="0" data-testid="cat-input-max-age" value={catForm.max_age || ''} onChange={(e) => setCatForm({ ...catForm, max_age: e.target.value })} /></Field>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setCatModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="cat-form-submit">حفظ</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmDelCat)} title="حذف الفئة" onClose={() => setConfirmDelCat(null)}>
        <p className="text-sm text-slate-600">
          {countFor(confirmDelCat?.id || '') > 0
            ? `تنبيه: الفئة «${confirmDelCat?.name}» مرتبطة بـ ${countFor(confirmDelCat?.id)} لاعب. حذفها قد يؤثر عليهم.`
            : `هل أنت متأكد من حذف «${confirmDelCat?.name}»؟`}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDelCat(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={removeCat} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="cat-confirm-del">حذف</button>
        </div>
      </Modal>

      {viewingCat && (
        <CategoryPlayersModal category={viewingCat} players={players} club={club} onClose={() => setViewingCat(null)} />
      )}
    </div>
  );
}
